import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../store/useAppStore';
import { questions } from '../data/questions';
import styles from './Review.module.css';

const Review: React.FC = () => {
  const navigate = useNavigate();
  const wrongQuestions = useAppStore((s) => s.wrongQuestions);
  const markWrongRetried = useAppStore((s) => s.markWrongRetried);

  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [selectedMultiOptions, setSelectedMultiOptions] = useState<string[]>([]);
  const [showResult, setShowResult] = useState(false);
  const [filterModule, setFilterModule] = useState<string>('all');

  // 未复习的错题
  const activeWrongs = wrongQuestions.filter((w) => !w.retryCorrect);

  // 按模块分组
  const modules = useMemo(() => {
    const map: Record<string, typeof activeWrongs> = {};
    activeWrongs.forEach((w) => {
      const q = questions.find((q) => q.id === w.questionId);
      if (q) {
        const moduleName = q.levelId.includes('feeding')
          ? '喂养'
          : q.levelId.includes('sleep')
          ? '睡眠'
          : '其他';
        if (!map[moduleName]) map[moduleName] = [];
        map[moduleName].push(w);
      }
    });
    return map;
  }, [activeWrongs]);

  const filteredWrongs =
    filterModule === 'all'
      ? activeWrongs
      : modules[filterModule] || [];

  const getQuestionById = (id: string) => questions.find((q) => q.id === id);

  const handleRetry = (questionId: string) => {
    setRetryingId(questionId);
    setSelectedAnswer(null);
    setSelectedMultiOptions([]);
    setShowResult(false);
  };

  const handleAnswer = () => {
    if (!retryingId) return;
    const q = getQuestionById(retryingId);
    if (!q) return;
    let correct = false;
    if (Array.isArray(q.correctAnswer)) {
      // 多选题：比较数组
      correct =
        selectedMultiOptions.length === q.correctAnswer.length &&
        q.correctAnswer.every((a) => selectedMultiOptions.includes(a));
    } else {
      correct = q.correctAnswer === selectedAnswer;
    }
    setShowResult(true);
    markWrongRetried(retryingId, correct);
  };

  const handleMultiToggle = (option: string) => {
    setSelectedMultiOptions((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const retryQuestion = retryingId ? getQuestionById(retryingId) : null;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          ← 返回
        </button>
        <h1 className={styles.title}>复习中心</h1>
        <p className={styles.subtitle}>
          {activeWrongs.length > 0
            ? `还有 ${activeWrongs.length} 道错题待复习`
            : '暂无待复习的错题，继续保持！'}
        </p>
        {/* 专项突破入口 */}
        {activeWrongs.length > 0 && (
          <button
            className={styles.challengeEntry}
            onClick={() => navigate('/challenge')}
          >
            <span className={styles.challengeIcon}>⚔️</span>
            <span className={styles.challengeLabel}>专项突破</span>
            <span className={styles.challengeDesc}>随机 5 题，检验薄弱点</span>
          </button>
        )}
      </header>

      {/* 重做模式 */}
      {retryingId && retryQuestion ? (
        <div className={styles.quizCard}>
          <button
            className={styles.cancelBtn}
            onClick={() => setRetryingId(null)}
          >
            ← 返回列表
          </button>
          <div className={styles.questionType}>
            {retryQuestion.type === 'single' && '单选题'}
            {retryQuestion.type === 'multiple' && '多选题'}
            {retryQuestion.type === 'truefalse' && '判断题'}
            {retryQuestion.type === 'scenario' && '情景题'}
          </div>
          <h2 className={styles.questionText}>{retryQuestion.text}</h2>
          <div className={styles.options}>
            {retryQuestion.type === 'multiple'
              ? retryQuestion.options?.map((opt, idx) => {
                  const isSelected = selectedMultiOptions.includes(opt);
                  return (
                    <button
                      key={idx}
                      className={`${styles.optionBtn} ${isSelected ? styles.optionSelected : ''}`}
                      onClick={() => handleMultiToggle(opt)}
                      disabled={showResult}
                    >
                      <span style={{ marginRight: 6, fontWeight: 700 }}>
                        {isSelected ? '☑' : '☐'}
                      </span>
                      {opt}
                    </button>
                  );
                })
              : retryQuestion.options?.map((opt, idx) => (
                  <button
                    key={idx}
                    className={`${styles.optionBtn} ${
                      selectedAnswer === opt ? styles.optionSelected : ''
                    }`}
                    onClick={() => setSelectedAnswer(opt)}
                    disabled={showResult}
                  >
                    {String.fromCharCode(65 + idx)}. {opt}
                  </button>
                ))}
          </div>
          {!showResult && (
            <button
              className={styles.confirmBtn}
              disabled={
                retryQuestion.type === 'multiple'
                  ? selectedMultiOptions.length === 0
                  : !selectedAnswer
              }
              onClick={handleAnswer}
            >
              确认答案
            </button>
          )}
          {showResult && (() => {
            const correctAnswer = retryQuestion.correctAnswer;
            const isCorrect = Array.isArray(correctAnswer)
              ? selectedMultiOptions.length === correctAnswer.length &&
                (correctAnswer as string[]).every((a: string) => selectedMultiOptions.includes(a))
              : selectedAnswer === correctAnswer;
            return (
            <div
              className={`${styles.feedback} ${isCorrect ? styles.correct : styles.wrong}`}
            >
              <p>
                {isCorrect ? '✓ 回答正确！' : '✗ 回答错误'}
              </p>
              <p className={styles.explainText}>{retryQuestion.explanation}</p>
              <button
                className={styles.backToListBtn}
                onClick={() => setRetryingId(null)}
              >
                返回列表
              </button>
            </div>
          );
        })()}
        </div>
      ) : (
        <>
          {/* 模块筛选 */}
          <div className={styles.filterBar}>
            <button
              className={`${styles.filterBtn} ${filterModule === 'all' ? styles.filterActive : ''}`}
              onClick={() => setFilterModule('all')}
            >
              全部 ({activeWrongs.length})
            </button>
            {Object.keys(modules).map((m) => (
              <button
                key={m}
                className={`${styles.filterBtn} ${filterModule === m ? styles.filterActive : ''}`}
                onClick={() => setFilterModule(m)}
              >
                {m} ({modules[m].length})
              </button>
            ))}
          </div>

          {/* 错题列表 */}
          {filteredWrongs.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>🎉</div>
              <p>这部分没有错题了，太棒了！</p>
            </div>
          ) : (
            <div className={styles.list}>
              {filteredWrongs.map((w) => {
                const q = getQuestionById(w.questionId);
                if (!q) return null;
                return (
                  <div key={w.questionId} className={styles.wrongItem}>
                    <div className={styles.wrongMeta}>
                      <span className={styles.wrongType}>
                        {q.type === 'single' && '单选'}
                        {q.type === 'multiple' && '多选'}
                        {q.type === 'truefalse' && '判断'}
                        {q.type === 'scenario' && '情景'}
                      </span>
                      <span className={styles.wrongDate}>
                        {new Date(w.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className={styles.wrongText}>{q.text}</p>
                    <button
                      className={styles.retryBtn}
                      onClick={() => handleRetry(w.questionId)}
                    >
                      再练一次
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* 今日建议 */}
      {!retryingId && activeWrongs.length > 0 && (
        <div className={styles.suggestion}>
          <h3>📋 今日建议复习</h3>
          <p>建议从错题最多的模块开始，每天复习 2-3 道错题，巩固薄弱知识点。</p>
        </div>
      )}
    </div>
  );
};

export default Review;